import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { apiCall } from '../../services/apiUtil';
import { roleLabel } from '../../squads/utils/squadRoles';

// Who a class invites by default, as the server has it: its players and coaches.
const CLASS_DEFAULT_ROLES = ['player', 'coach'];

/**
 * WHO IS INVITED TO AN EVENT, AND CHANGING IT.
 *
 * Whoever runs a squad event ticks and unticks the squad's members. Somebody
 * who has already answered stays on — their answer is the record of what they
 * said — so only unanswered invitations can be taken away. For a series the
 * change can apply to this date, or to this and every later one.
 *
 * Props:
 * - event: the event, with its responses (one row per person invited or answered)
 * - squadMembers: the squad's members, to pick from
 * - onSaved: called after a successful change, to re-read the events
 * - defaultScope: 'this' or 'future' — what a series change starts out applying
 *   to. Opened from a whole series it is every upcoming date.
 */
const EventInviteesModal = ({
  isOpen, onClose, event, squadMembers = [], onSaved, defaultScope = 'this', isClassSquad = false,
}) => {
  const { theme } = useTheme();
  const people = useMemo(() => (squadMembers || [])
    .filter(m => m.userId && m.status === 'active')
    .map(m => ({ userId: String(m.userId), name: m.username, role: m.role })), [squadMembers]);

  // An event made before invite lists is for the whole squad. It starts from
  // everybody the squad invites today — as the server does when this is saved —
  // plus anybody who has already answered.
  const unlisted = event?.audience !== 'invitees';
  const rows = useMemo(() => {
    const answers = (event?.responses || []).map(r => [String(r.userId), r.status]);
    if (!unlisted) return new Map(answers);
    const everyone = people
      .filter(p => !isClassSquad || CLASS_DEFAULT_ROLES.includes(p.role))
      .map(p => [p.userId, 'invited']);
    return new Map([...everyone, ...answers]);
  }, [event?.responses, unlisted, people, isClassSquad]);
  const [ticked, setTicked] = useState(() => new Set());
  const [scope, setScope] = useState('this');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTicked(new Set(rows.keys()));
    setScope(defaultScope);
  }, [isOpen, rows, defaultScope]);

  if (!isOpen || !event) return null;

  const answered = (userId) => rows.has(userId) && rows.get(userId) !== 'invited';
  const toggle = (userId) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    return next;
  });
  const add = people.filter(p => ticked.has(p.userId) && !rows.has(p.userId)).map(p => p.userId);
  const remove = people.filter(p => !ticked.has(p.userId) && rows.get(p.userId) === 'invited').map(p => p.userId);
  const changed = add.length + remove.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      await apiCall(`/events/${event.id}/invitees`, {
        method: 'PUT',
        body: JSON.stringify({ add, remove, scope }),
      });
      toast.success('Invitations updated');
      if (onSaved) await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Could not update invitations');
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`${theme.card} rounded-lg max-w-md w-full max-h-[80vh] flex flex-col`}>
        <div className={`flex items-center justify-between p-4 border-b ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text}`}>Who's invited ({ticked.size})</h3>
          <button onClick={onClose} aria-label="Close" title="Close" className={`p-2 rounded-full ${theme.cardHover}`}>
            <X className={`w-5 h-5 ${theme.text}`} />
          </button>
        </div>

        {unlisted && (
          <p className={`px-4 pt-3 text-xs ${theme.textMuted}`}>
            This event is open to the whole squad. Saving gives it its own list, starting from who is ticked.
          </p>
        )}

        <ul className="p-4 space-y-1 overflow-y-auto flex-1 min-h-0">
          {people.map(p => (
            <li key={p.userId}>
              <label className={`flex items-center gap-x-2 text-sm ${theme.text} ${answered(p.userId) ? 'opacity-70' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={ticked.has(p.userId)}
                  disabled={answered(p.userId)}
                  onChange={() => toggle(p.userId)}
                  aria-label={`Invite ${p.name}`}
                />
                <span className="flex-1">{p.name}</span>
                <span className={`text-xs ${theme.textMuted}`}>{answered(p.userId) ? 'Answered' : roleLabel(p.role)}</span>
              </label>
            </li>
          ))}
        </ul>

        <div className={`p-4 border-t ${theme.border} flex items-center justify-between gap-x-2`}>
          {event.recurrenceRuleId ? (
            <select
              aria-label="Apply to"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className={`text-sm rounded-lg border ${theme.border} ${theme.input} px-2 py-1`}
            >
              <option value="this">This date only</option>
              <option value="future">This and future dates</option>
            </select>
          ) : <span />}
          <button
            onClick={save}
            disabled={!changed || saving}
            className={`${theme.buttonPrimary} px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50`}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventInviteesModal;
